import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core'
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings'
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined'
import ContactsOutlined from '@mui/icons-material/ContactsOutlined'
import EditOutlined from '@mui/icons-material/EditOutlined'
import MailOutline from '@mui/icons-material/MailOutlined'
import MenuIcon from '@mui/icons-material/Menu'
import { Alert, Avatar, Box, Button, Chip, Dialog, DialogContent, DialogTitle, Drawer, Fab, IconButton, Snackbar, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Suspense, lazy, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useActions, useMe, useSetLabels, useSettings } from '../api/hooks'
import { colorFor, initials } from '../lib/color'
import { useRealtime } from '../lib/useRealtime'
import { useShortcuts } from '../lib/useShortcuts'
import { useUi } from '../store/ui'
import { useTokens } from '../theme'
import { Wordmark } from '../lib/Wordmark'
import { AccountMenu } from './AccountMenu'
import { DialogHost } from './DialogHost'
import { Dock } from './Dock'
import { SearchBox } from './SearchBox'
import { appOf } from './apps'
import { SidePanel } from './SidePanel'
import { Sidebar } from './Sidebar'

const ComposeDock = lazy(() => import('../compose/ComposeDock'))
const HELP: Array<[string, string]> = [['c', 'Compose'], ['/', 'Search'], ['j / k', 'Next / previous message'], ['o / Enter', 'Open message'], ['x', 'Select'], ['e', 'Archive'], ['#', 'Delete'], ['!', 'Report spam'], ['l', 'Label'], ['s', 'Star'], ['u', 'Back to list'], ['Shift+I / Shift+U', 'Mark read / unread'], ['r / a / f', 'Reply / reply all / forward'], ['?', 'This help']]

export function AppShell() {
  const theme = useTheme()
  const t = useTokens()
  const desktop = useMediaQuery(theme.breakpoints.up('md'))
  const me = useMe()
  const settings = useSettings()
  const navigate = useNavigate()
  const location = useLocation()
  const searchRef = useRef<HTMLInputElement>(null)
  const { drawerOpen, setDrawerOpen, openCompose, snackbars, dismiss, shortcutsHelp, setShortcutsHelp, selected, sidebarCollapsed, toggleSidebar } = useUi()
  const [account, setAccount] = useState<HTMLElement | null>(null)
  const [dragging, setDragging] = useState<string[] | null>(null)
  const actions = useActions()
  const labels = useSetLabels()
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))
  const app = appOf(location.pathname)

  useRealtime(Boolean(me.data))
  const focusSearch = () => { if (desktop && sidebarCollapsed) toggleSidebar(); setTimeout(() => searchRef.current?.focus(), 0) }
  useShortcuts({ c: () => openCompose(), '/': focusSearch, '?': () => setShortcutsHelp(true) }, settings.data?.shortcuts ?? true)

  const onDragStart = (e: DragStartEvent) => { const id = String(e.active.id).replace(/^msg:/, ''); setDragging(selected.has(id) ? [...selected] : [id]) }
  const onDragEnd = (e: DragEndEvent) => {
    const ids = dragging; setDragging(null)
    const over = e.over ? String(e.over.id) : null
    if (!ids || !over) return
    if (over.startsWith('label:')) labels.apply(ids, [over.slice(6)], [])
    else if (over.startsWith('folder:')) actions.run(ids, 'move', { destination: over.slice(7), toast: `Moved ${ids.length} conversation${ids.length > 1 ? 's' : ''}` })
  }
  const snack = snackbars[0]
  const email = me.data?.email ?? ''
  const appLinks = [
    { label: 'Mail', to: '/mail/f/inbox', icon: <MailOutline /> },
    { label: 'Calendar', to: '/calendar', icon: <CalendarMonthOutlined /> },
    { label: 'Contacts', to: '/contacts', icon: <ContactsOutlined /> },
    ...(me.data?.role === 'ADMIN' ? [{ label: 'Admin', to: '/admin', icon: <AdminPanelSettings /> }] : []),
  ]

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
      <Box sx={{ display: 'flex', flexDirection: desktop ? 'row' : 'column', height: '100%', bgcolor: t.canvas }}>
        {desktop ? (
          <Dock collapsed={sidebarCollapsed} searchRef={searchRef} />
        ) : (
          <Box component="header" sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 56, px: 1, flexShrink: 0 }}>
            <IconButton aria-label="Main menu" onClick={() => setDrawerOpen(true)}><MenuIcon /></IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}><SearchBox inputRef={searchRef} /></Box>
            <Tooltip title={email}><IconButton aria-label="Account menu" onClick={(e) => setAccount(e.currentTarget)}><Avatar sx={{ width: 32, height: 32, bgcolor: colorFor(email), fontSize: 14 }}>{initials(null, email)}</Avatar></IconButton></Tooltip>
            <AccountMenu anchor={account} onClose={() => setAccount(null)} email={email} />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', py: desktop ? 1 : 0, pr: desktop ? 1 : 0 }}>
          <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', bgcolor: 'background.paper', overflow: 'hidden', borderRadius: desktop ? '20px' : 0, border: desktop ? 1 : 0, borderColor: 'divider' }}><Outlet /></Box>
          {desktop && app === 'mail' && <SidePanel />}
        </Box>
      </Box>

      <Drawer open={!desktop && drawerOpen} onClose={() => setDrawerOpen(false)} slotProps={{ paper: { sx: { width: 280, bgcolor: t.rail } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}><Box component="img" src="/logo.png" alt="" sx={{ width: 28, height: 28 }} /><Wordmark size={22} /></Box>
        <Sidebar onNavigate={() => setDrawerOpen(false)} />
        <Box sx={{ p: 1 }}>{appLinks.slice(1).map((a) => <Button key={a.label} fullWidth startIcon={a.icon} sx={{ justifyContent: 'flex-start', color: 'text.primary', px: 2 }} onClick={() => { setDrawerOpen(false); navigate(a.to) }}>{a.label}</Button>)}</Box>
      </Drawer>

      {!desktop && app === 'mail' && <Fab color="primary" aria-label="Compose" onClick={() => openCompose()} sx={{ position: 'fixed', right: 16, bottom: 16, bgcolor: t.composeBg, color: t.composeFg, '&:hover': { bgcolor: t.composeBg } }}><EditOutlined /></Fab>}
      <DialogHost />
      <Suspense fallback={null}><ComposeDock /></Suspense>
      <DragOverlay>{dragging ? <Chip color="primary" label={`${dragging.length} conversation${dragging.length > 1 ? 's' : ''}`} /> : null}</DragOverlay>

      <Snackbar key={snack?.id} open={Boolean(snack)} autoHideDuration={snack?.durationMs ?? (snack?.onAction ? 7000 : 4000)} onClose={(_, reason) => { if (reason !== 'clickaway' && snack) dismiss(snack.id) }} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        {snack ? <Alert variant="filled" severity={snack.severity ?? 'info'} icon={false} sx={{ alignItems: 'center' }} action={snack.actionLabel ? <Button color="inherit" size="small" onClick={() => { snack.onAction?.(); dismiss(snack.id) }}>{snack.actionLabel}</Button> : undefined}>{snack.message}</Alert> : undefined}
      </Snackbar>

      <Dialog open={shortcutsHelp} onClose={() => setShortcutsHelp(false)}>
        <DialogTitle>Keyboard shortcuts</DialogTitle>
        <DialogContent>{HELP.map(([k, d]) => <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 4, py: 0.5 }}><Typography>{d}</Typography><Typography component="kbd" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, borderRadius: 1 }}>{k}</Typography></Box>)}</DialogContent>
      </Dialog>
    </DndContext>
  )
}
